import React, { useMemo } from 'react'
import { Button, Chip, Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.25),
        border: '1px solid #dbe4f0',
        borderRadius: 10,
        background: '#f8fafc',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
    },
    title: {
        fontSize: 13,
        fontWeight: 600,
        color: '#0f172a',
    },
    meta: {
        fontSize: 12,
        lineHeight: '17px',
        color: '#64748b',
    },
    action: {
        marginTop: theme.spacing(0.75),
        padding: 0,
        minWidth: 0,
        textTransform: 'none',
        fontWeight: 600,
    },
}))

const TERMINAL_STATUS_NAMES = ['списан', 'выполнен']

const asArray = (value) => {
    if (Array.isArray(value)) return value
    if (value && typeof value === 'object') return Object.values(value)
    return []
}

const getOrders = (clientInfo) => {
    const source = clientInfo?.orderListKlient?.OrderList
        || clientInfo?.orderListKlient
        || {}

    if (Array.isArray(source)) return source

    return Object.keys(source)
        .sort((a, b) => Number(b) - Number(a))
        .reduce((result, dateKey) => {
            const ordersForDate = [...asArray(source[dateKey])]
                .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0))
            return result.concat(ordersForDate)
        }, [])
}

const getStatusName = (order, statusReferences) => {
    const directName = order?.statusOrder?.name || order?.statusOrder?.status_name
    if (directName) return String(directName).trim()

    const statusId = String(order?.statusOrder?.id_status ?? '')
    if (!statusId) return ''

    const status = asArray(statusReferences)
        .find((item) => String(item?.id ?? '') === statusId)
    return String(status?.name || '').trim()
}

const normalizeStatusName = (value) => String(value || '').trim().toLowerCase().replace(/ё/g, 'е')

const getDeliveryLabel = (order) => {
    const date = String(order?.delivered?.dateDeliveredText || '').trim()
    const time = String(order?.delivered?.timeDeliveredText || '').trim()
    return [date, time].filter(Boolean).join(' · ')
}

const formatMoney = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return ''
    const number = Number(String(value ?? '').replace(/\s/g, '').replace(',', '.'))
    if (!Number.isFinite(number)) return ''
    return `${number.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`
}

const CurrentClientOrderCard = ({
    clientInfo,
    statusReferences = [],
    onOpenOrder,
    canOpenOrder = true,
    actionLabel = 'Открыть заказ',
    unavailableReason = 'Нет доступа к просмотру заказов',
}) => {
    const classes = useStyles()
    const currentOrderInfo = useMemo(() => {
        const references = [
            ...asArray(clientInfo?.orderListKlient?.AllStatusOrder),
            ...asArray(statusReferences),
        ]
        const order = getOrders(clientInfo)
            .find((order) => {
                if (Number(order?.deleted || 0) === 1) return false
                const statusName = normalizeStatusName(getStatusName(order, references))
                if (!statusName) return false
                return !TERMINAL_STATUS_NAMES.includes(statusName)
            }) || null

        if (!order) return null
        return {
            order,
            statusName: getStatusName(order, references) || 'Текущий',
        }
    }, [clientInfo, statusReferences])

    if (!currentOrderInfo) return null

    const {order: currentOrder, statusName} = currentOrderInfo
    const orderId = Number(currentOrder?.id || 0)
    const numberOrder = Number(currentOrder?.numberOrder || currentOrder?.number_order || orderId)
    const amount = formatMoney(currentOrder?.ItogSumOrder)
    const delivery = getDeliveryLabel(currentOrder)

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography className={classes.title}>
                    Текущий заказ{numberOrder > 0 ? ` #${numberOrder}` : ''}
                </Typography>
                <Chip size="small" variant="outlined" label={statusName} />
            </div>
            {amount ? <Typography className={classes.meta}>Сумма: {amount}</Typography> : null}
            {delivery ? <Typography className={classes.meta}>Получение: {delivery}</Typography> : null}
            {orderId > 0 && typeof onOpenOrder === 'function' ? (
                <Tooltip title={canOpenOrder ? '' : unavailableReason}>
                    <span>
                        <Button
                            className={classes.action}
                            size="small"
                            color="primary"
                            disabled={!canOpenOrder}
                            onClick={() => {
                                if (canOpenOrder) onOpenOrder(orderId, currentOrder)
                            }}
                        >
                            {actionLabel}
                        </Button>
                    </span>
                </Tooltip>
            ) : null}
        </div>
    )
}

export default CurrentClientOrderCard
